import { combineReducers } from 'redux'
import { sendingActions } from 'store/sender'
import request, { generateNotyMessage } from 'utils/request'
import STRINGS from 'utils/strings'
import { DAYS_IN_YEAR } from 'utils/time'
import { actions as notyActions } from 'layouts/ErrorBox'
import { onRedirectVisit } from '../../VisitTemplatePage/utils/visitRedirection'
import { getCreateTemplateVisitErrors } from '../../VisitTemplatePage/utils/visitTemplateValidation'
import { SET_SCHEDULE_VISIT_TEMPLATES } from '../../../../VisitSchedulesPage/modules/VisitSchedules'
import { SET_VISIT_TEMPLATE } from '../../VisitTemplatePage/modules/VisitTemplate'
import { getStudyVersionID } from 'utils/studyConfigUtils'
import { getStudyDurationForTimeline, getStudyDurationFromStudy } from 'utils/studyDurationForTimeline'
import {
  addPriorVisits,
  doesVisitDayExceedSelectedCycleDuration,
  getComputedStudyDay,
  getCyclesWithResolvedProjectedDays,
  getSelectedCycleDurationDays,
} from '../utils/visitDayUtils'
import { getAllCycles } from '../../../../VisitSchedulesPage/utils/visitsScheduleUtils'

//
// Actions
//
const INIT_TEMPLATE_VISIT = 'INIT_TEMPLATE_VISIT'
const UPDATE_TEMPLATE_VISIT = 'UPDATE_TEMPLATE_VISIT'
const UPDATE_TEMPLATE_VISIT_FIELDS = 'UPDATE_TEMPLATE_VISIT_FIELDS'
const UPDATE_TEMPLATE_VISIT_WINDOW = 'UPDATE_TEMPLATE_VISIT_WINDOW'
const UPDATE_TEMPLATE_VISIT_METADATA = 'UPDATE_TEMPLATE_VISIT_METADATA'
const UPDATE_VISIT_CYCLE_TEMPLATE = 'UPDATE_VISIT_CYCLE_TEMPLATE'
const UPDATE_ORIGINAL_TEMPLATE_VISIT = 'UPDATE_ORIGINAL_TEMPLATE_VISIT'
const SET_TEMPLATE_VISIT = 'SET_TEMPLATE_VISIT'

// ERRORS
const ADD_TEMPLATE_VISIT_ERRORS = 'ADD_TEMPLATE_VISIT_ERRORS'
const CLEAR_TEMPLATE_VISIT_ERRORS = 'CLEAR_TEMPLATE_VISIT_ERRORS'
//
// Action Creators
//

const initPage = () => dispatch => {
  return dispatch({
    type: INIT_TEMPLATE_VISIT,
  })
}

const updateVisit = ({ key, value }) => dispatch => dispatch({ type: UPDATE_TEMPLATE_VISIT, key, value })

const updateVisitFields = ({ key1, value1, key2, value2 }) => dispatch =>
  dispatch({ type: UPDATE_TEMPLATE_VISIT_FIELDS, key1, value1, key2, value2 })

const updateVisitWindow = ({ value }) => dispatch => dispatch({ type: UPDATE_TEMPLATE_VISIT_WINDOW, value })

const updateVisitMetadata = ({ key, value }) => dispatch =>
  dispatch({ type: UPDATE_TEMPLATE_VISIT_METADATA, key, value })

const updateVisitCycle = ({ key, value }) => dispatch => dispatch({ type: UPDATE_VISIT_CYCLE_TEMPLATE, key, value })

const addVisitErrors = errors => dispatch => dispatch({ type: ADD_TEMPLATE_VISIT_ERRORS, errors })

const clearVisitErrors = () => dispatch => dispatch({ type: CLEAR_TEMPLATE_VISIT_ERRORS })
//
// Utils
//
const defaultVisitCycle = {
  vcName: '',
  cycleDuration: 28,
  cycleDurationTime: '',
  cycleOccur: 0,
  cycleOccurTime: 0,
  afterCycle: '',
  afterVisitCycleValue: '',
}

const defaultTemplateVisitState = {
  name: '',
  visit_day: null,
  visit_metadata: {
    display_name: '',
    reminders: {},
  },
  visit_window: null,
  // visitCycle: {},
}

//
// API functions
//
const saveTemplateVisitRequest = ({
  studyID,
  body,
  visitTemplateId,
  isUpdate,
  onSuccess,
  isVisitCycle,
  visitId,
}) => dispatch => {
  const visitCycleUrl = `/control/v2/studies/${studyID}/study_schedules/${visitTemplateId}${
    !isUpdate ? '/visits' : `/visits/${visitId}`
  }`
  const url = `/control/studies/${studyID}/study_schedules/${visitTemplateId}${!isUpdate ? '/visits' : ``}`

  const fail = (res, content) => {
    const msg = content?.message || STRINGS.visitSavedFail
    dispatch(
      notyActions.showError({
        text: generateNotyMessage(msg, false),
      }),
    )
  }

  return dispatch(
    request({
      url: isVisitCycle ? visitCycleUrl : url,
      body,
      method: isUpdate ? 'PATCH' : 'POST',
      successMessage: STRINGS.visitSavedSuccess,
      fail,
      success: onSuccess,
      hasSender: true,
    }),
  )
}

const onDeleteVisit = ({ studyID, templateVisitId, visitScheduleId }) => dispatch => {
  const id = visitScheduleId?.includes('_') ? visitScheduleId?.split('_')?.[0] : visitScheduleId
  const url = `/control/studies/${studyID}/study_schedules/${id}/visit/${templateVisitId}`
  const success = resJSON => {
    dispatch(onRedirectVisit({ resJSON, studyID }))
  }

  return dispatch(
    request({
      url,
      method: 'DELETE',
      successMessage: STRINGS.visitDelSuccess,
      failMessage: STRINGS.visitDelFail,
      success,
    }),
  )
}

//
// Util Functions
//
const saveTemplateVisit = ({
  studyID,
  templateVisit,
  isUpdate,
  visitTemplateId,
  onRedirect,
  visitId,
  onSuccessCallback,
}) => (dispatch, getState) => {
  const studyVersionID = getStudyVersionID(getState()?.location)
  const hasStudyPromotion = !!getState()?.study?.currentStudy?.config?.study_promotion
  const onSuccess = resJSON => {
    dispatch(sendingActions.stopSender())
    dispatch({
      type: UPDATE_ORIGINAL_TEMPLATE_VISIT,
      json: templateVisit,
    })
    setTimeout(() => {
      dispatch(sendingActions.resetSender())
      onRedirect(resJSON)
    }, 500)
    if (onSuccessCallback) {
      onSuccessCallback()
    }
  }

  const body = JSON.stringify(
    isUpdate
      ? {
          study_visit_schedule: { visits: [templateVisit] },
          study_version_id: hasStudyPromotion ? studyVersionID : undefined,
        }
      : { visits: [templateVisit], study_version_id: hasStudyPromotion ? studyVersionID : undefined },
  )

  const { study } = getState() || {}
  const isVisitCycle = study?.currentStudy?.config?.visit_support?.version > 1

  const metadata = {
    ...templateVisit?.metadata,
    ...templateVisit?.visit_metadata,
  }

  const getVisitWindow = () => {
    if (!templateVisit?.visit_window && !templateVisit?.window) {
      return 0
    }
    return templateVisit?.visit_window || templateVisit?.window
  }

  const updateVisitBody =
    isVisitCycle && isUpdate
      ? {
          name: templateVisit?.name,
          visit_day: templateVisit?.day ?? templateVisit?.visit_day,
          visit_metadata: metadata,
          visit_window: getVisitWindow(),
          // study_phase_id: templateVisit?.study_phase_id,
          // study_cycle_id: templateVisit?.cycle_id,
          study_phase_cycle_id: templateVisit?.study_phase_cycle_id || '',
          study_version_id: hasStudyPromotion ? studyVersionID : undefined,
        }
      : {}

  return dispatch(
    saveTemplateVisitRequest({
      studyID,
      body: isVisitCycle && isUpdate ? JSON.stringify(updateVisitBody) : body,
      visitTemplateId,
      isUpdate,
      onSuccess,
      isVisitCycle,
      visitId,
    }),
  )
}

const onSaveTemplateVisit = ({
  studyID,
  redirectPath,
  queryParams,
  isUpdate,
  visitTemplateId,
  templateVisitId,
  selectedCycleId,
  onSuccessCallback,
}) => (dispatch, getState) => {
  const { templateVisitReducer, subroute, study, visitSchedules } = getState()
  const isVisitCycle = study?.currentStudy?.config?.visit_support?.version > 1
  const { templateVisit } = templateVisitReducer
  const errors = getCreateTemplateVisitErrors(templateVisit, study?.currentStudy)
  // UI-selected cycle is the source of truth; never fall back to stale Redux selection or dummy-cycle ids.
  const resolvedCycleId = selectedCycleId || ''

  // Additional validation: for visit-cycle schedules, block visits whose computed study day exceeds study duration
  if (isVisitCycle) {
    const deploymentOption =
      templateVisit?.visit_metadata?.deployment_option || templateVisit?.metadata?.deployment_option

    if (deploymentOption && deploymentOption !== 'joins') {
      const studyDurationPayload =
        getStudyDurationForTimeline() || getStudyDurationFromStudy(study?.currentStudy || null)
      const studyDurationInDays = studyDurationPayload?.study_duration

      if (typeof studyDurationInDays === 'number' && Number.isFinite(studyDurationInDays)) {
        const arms = visitSchedules?.studyVisitSchedule || []
        const arm = arms.find(visitSchedule => visitSchedule.id === visitTemplateId)
        const allCycles =
          arm?.phases?.reduce((acc, phase) => {
            return [...acc, ...(phase?.cycles || [])]
          }, []) || []

        const visitDayValue = templateVisit?.day ?? templateVisit?.visit_day
        const anchorCycleId = templateVisit?.visit_metadata?.relative_cycle_id
        const anchorVisitId = templateVisit?.visit_metadata?.relative_visit_id

        const computedStudyDay = getComputedStudyDay({
          allCycles,
          selectedCycleId: resolvedCycleId,
          visitDayValue,
          selectedVisitId: templateVisit?.id,
          anchorCycleId,
          anchorVisitId,
          deploymentOption,
        })
        const selectedCycleDurationDays = getSelectedCycleDurationDays({ allCycles, selectedCycleId: resolvedCycleId })

        if (doesVisitDayExceedSelectedCycleDuration({ allCycles, selectedCycleId: resolvedCycleId, visitDayValue })) {
          errors.visit_day = STRINGS.visitDayExceedsCycleDuration.replace(
            '{value}',
            String(selectedCycleDurationDays),
          )
        } else if (
          typeof computedStudyDay === 'number' &&
          Number.isFinite(computedStudyDay) &&
          computedStudyDay > studyDurationInDays
        ) {
          errors.visit_day = STRINGS.visitDayExceedsStudyDuration.replace(
            '{value}',
            String(studyDurationInDays),
          )
        }
      }
    }
  }
  const valid = !(Object.values(errors).filter(val => val).length >= 1)
  const onRedirect = resJSON =>
    dispatch(onRedirectVisit({ resJSON, redirectPath, studyID, queryParams, visitScheduleSubroute: subroute }))

  if (valid) {
    dispatch(clearVisitErrors())
    dispatch(sendingActions.startSender())
    // validate if templateVisit visit tolerance is null
    const { visit_window } = templateVisit
    if (visit_window === '' || visit_window === null) {
      templateVisit.visit_window = 0
    }
    if (isVisitCycle) {
      const phases = visitSchedules?.studyVisitSchedule?.filter(
        visitSchedule => visitSchedule.id === visitTemplateId,
      )[0]?.phases

      if (phases?.[0]?.id) {
        templateVisit.study_phase_id = phases[0]?.id
      }

      templateVisit.study_phase_cycle_id = resolvedCycleId

      const deploymentOption =
        templateVisit?.visit_metadata?.deployment_option || templateVisit?.metadata?.deployment_option
      if (!resolvedCycleId && (!deploymentOption || deploymentOption === 'joins')) {
        templateVisit.anchor_id = ''
      }

      if (visitSchedules?.selectedVisitCycle) {
        // templateVisit.visitCycle = visitSchedules?.selectedVisitCycle
      }
    }
    dispatch(
      saveTemplateVisit({
        studyID,
        ...templateVisitReducer,
        isUpdate,
        visitTemplateId,
        onRedirect,
        visitId: templateVisitId,
        onSuccessCallback,
      }),
    )
  } else {
    dispatch(addVisitErrors(errors))
    const errorMessage = errors?.visit_day || STRINGS.pleaseCheckErr
    dispatch(
      notyActions.showError({
        text: generateNotyMessage(errorMessage, false),
      }),
    )
  }
}

const selectVisit = (visitTemplate, templateVisitId, phaseId, cycleId) => {
  if (templateVisitId) {
    if (phaseId) {
      const phases = Array.isArray(visitTemplate?.phases) ? visitTemplate.phases : []
      const phase = phases.find(p => p.id === phaseId)
      if (!phase) return null
      const cycles = Array.isArray(phase?.cycles) ? phase.cycles : []
      const cycle = cycles.find(c => c.id === cycleId)
      if (!cycle) return null
      const visits = Array.isArray(cycle?.visits) ? cycle.visits : []
      const visit = visits.find(v => v.id === templateVisitId)
      return visit || null
    } else {
      const visits = Array.isArray(visitTemplate?.visits) ? visitTemplate.visits : []
      const indexOfTemplateVisit = visits.findIndex(visit => visit.id === templateVisitId)
      return visits[indexOfTemplateVisit] || null
    }
  }
  return defaultTemplateVisitState
}
// TODO
const formatVisitSchedules = visitSchedulesList => {
  return visitSchedulesList.map(visitTemplate => {
    const { id, name } = visitTemplate
    return { key: id, text: name }
  })
}

const formatScheduleVisitTemplatesList = (visitSchedulesTemplatesList, isVisitCycle) => {
  const resultObj = {}
  const allCyclesResolved = getCyclesWithResolvedProjectedDays(getAllCycles(visitSchedulesTemplatesList))

  visitSchedulesTemplatesList.forEach(({ visits, id: visitScheduleId, phases }) => {
    const visitTableMap = {}
    const visitOptions = []
    const visitDayMap = {}

    if (isVisitCycle) {
      const phaseList = Array.isArray(phases) ? phases : []
      phaseList.forEach(phase => {
        phase?.cycles?.forEach(cycle => {
          cycle?.visits?.forEach(({ name: visitName, window, day: visit_day, id: visitId, metadata = {} }) => {
            const computedStudyDay = getComputedStudyDay({
              allCycles: allCyclesResolved,
              selectedCycleId: cycle?.is_dummy ? '' : cycle?.id,
              visitDayValue: visit_day,
              selectedVisitId: visitId,
              anchorCycleId: metadata?.relative_cycle_id,
              anchorVisitId: metadata?.relative_visit_id,
              deploymentOption: metadata?.deployment_option,
            })
            const row = [
              { key: 'visitName', value: visitName, sortValue: visitName, visitId },
              {
                key: 'visitDay',
                value: computedStudyDay,
                sortValue: computedStudyDay,
                className: 'visit-day-col',
              },
            ]
            visitTableMap[visitId] = row
            visitOptions.push({ key: visitId, text: visitName, visitDay: visit_day, cycleId: cycle?.id })
            visitDayMap[visitId] = visit_day
          })
        })
      })
    } else {
      const visitList = Array.isArray(visits) ? visits : []
      visitList.forEach(({ id, name, visit_day }, _idx, arr) => {
        const priorVisits = addPriorVisits(arr, id)
        const visitDay = Object.values(priorVisits).reduce((prev, curr) => prev + curr, 0)
        const row = [
          { key: 'visitName', value: name, sortValue: name, visitId: id },
          {
            key: 'visitDay',
            value: visitDay,
            sortValue: visitDay,
            className: 'visit-day-col',
          },
        ]
        visitTableMap[id] = row
        visitOptions.push({ key: id, text: name, visitDay: visit_day })
        visitDayMap[id] = visit_day
      })
    }

    resultObj[visitScheduleId] = { visitDayMap, visitTableMap, visitOptions }
  })
  return resultObj
}

//
// Reducers
//

const templateVisit = (state = defaultTemplateVisitState, action) => {
  const newState = { ...state }
  switch (action.type) {
    case INIT_TEMPLATE_VISIT:
      return defaultTemplateVisitState
    case SET_VISIT_TEMPLATE: {
      return selectVisit(action.visitTemplate, action.templateVisitId, action.phaseId, action.cycleId)
    }
    case UPDATE_TEMPLATE_VISIT:
      return { ...newState, [action.key]: action.value }
    case UPDATE_TEMPLATE_VISIT_FIELDS:
      return { ...newState, [action.key1]: action.value1, [action.key2]: action.value2 }
    case UPDATE_TEMPLATE_VISIT_WINDOW:
      return { ...newState, visit_window: action.value, window: action.value }
    case UPDATE_TEMPLATE_VISIT_METADATA:
      return {
        ...newState,
        visit_metadata: { ...newState.visit_metadata, ...newState.metadata, [action.key]: action.value },
      }
    case SET_TEMPLATE_VISIT: {
      return action.visitTemplate
    }
    default:
      return state
  }
}

const templateVisitCycle = (state = defaultVisitCycle, action) => {
  const newState = { ...state }
  switch (action.type) {
    case UPDATE_VISIT_CYCLE_TEMPLATE:
      return { ...newState, [action.key]: action.value }
    default:
      return state
  }
}

const templateVisitErrors = (state = {}, action) => {
  switch (action.type) {
    case INIT_TEMPLATE_VISIT:
    case CLEAR_TEMPLATE_VISIT_ERRORS:
      return {}
    case ADD_TEMPLATE_VISIT_ERRORS:
      return action.errors
    default:
      return state
  }
}

const originalTemplateVisit = (state = {}, action) => {
  if (action.type === INIT_TEMPLATE_VISIT || action.type === UPDATE_ORIGINAL_TEMPLATE_VISIT) {
    return JSON.parse(JSON.stringify(action.json || defaultTemplateVisitState))
  }
  return state
}

const visitScheduleOptions = (state = [], action) => {
  switch (action.type) {
    case SET_SCHEDULE_VISIT_TEMPLATES:
      return formatVisitSchedules(action.payload)
    default:
      return state
  }
}

const visitScheduleInitialData = (state = {}, action) => {
  switch (action.type) {
    case SET_SCHEDULE_VISIT_TEMPLATES:
      return formatScheduleVisitTemplatesList(action.payload, action.isVisitCycle)
    default:
      return state
  }
}

export default combineReducers({
  templateVisit,
  templateVisitCycle,
  templateVisitErrors,
  originalTemplateVisit,
  visitScheduleInitialData,
  visitScheduleOptions,
})

export const visitActions = {
  initPage,
  onDeleteVisit,
  onSaveTemplateVisit,
  updateVisit,
  updateVisitFields,
  updateVisitWindow,
  updateVisitMetadata,
  updateVisitCycle,
}
